import * as React from 'react';

import { CheckCircleOutlined, HomeOutlined, UnorderedListOutlined, UserOutlined } from '@ant-design/icons';
import { HomePage, NotFoundPage } from 'src/routes/pages';
import { Navigate, Route, Routes } from 'react-router-dom';

import { AppRoute } from 'src/types/appRoute';
import { InterviewCompletedPage } from 'src/routes/pages/interviewCompletedPage';
import { InterviewPage } from 'src/routes/pages/interviewPage';
import { IntroPage } from 'src/routes/pages/introPage';
import { getPath } from 'src/utility/path';
import { useAppSelector } from 'src/types/reduxHelpers';

// Helper type to allow for autocomplete AppRoute values when adding routes below, while still allowing property access elsewhere
// See: https://stackoverflow.com/a/73966420/3253311
const typeObjectValues = <T extends Record<string, AppRoute>>(object: T): { [originalPropType in keyof T]: AppRoute } => {
    type propType = keyof typeof object;
    return object as { [originalPropType in propType]: AppRoute };
};

// Add available routes here
// Can be secured with isProtected and allowedRoles props
export const appRoutes = typeObjectValues({
    home: {
        title: 'Home',
        titleKey: 'Pages.HomePage.title',
        path: getPath(''),
        component: <HomePage />,
        icon: <HomeOutlined />,
    },
    intro: {
        title: 'Start',
        titleKey: 'Pages.IntroPage.title',
        path: getPath('/intro'),
        component: <IntroPage />,
        icon: <UnorderedListOutlined />,
        isTokenRequired: true,
    },
    interview: {
        title: 'Personal Info',
        titleKey: 'Pages.InterviewPage.title',
        path: getPath('/interview'),
        component: <InterviewPage />,
        icon: <UserOutlined />,
        isTokenRequired: true,
    },
    interviewCompleted: {
        title: 'Interview Completed',
        titleKey: 'Pages.InterviewCompletedPage.title',
        path: getPath('/interview/completed'),
        component: <InterviewCompletedPage />,
        icon: <CheckCircleOutlined />,
    },
});

export type RouteType = typeof appRoutes;

export const AppRoutes: React.FC = (props) => {
    const token = useAppSelector((state) => state.invitation.token);

    const getRouteElement = (appRoute: AppRoute): React.ReactNode => {
        if (appRoute.isTokenRequired && !token) {
            return (
                <Navigate to={appRoutes.home.path} />
            );
        }

        return appRoute.component;
    };

    return (
        <>
            <Routes>
                {Object.entries(appRoutes).map((entry) => {
                    const [_routeName, appRoute] = entry;

                    return (
                        <Route
                            key={appRoute.path}
                            path={appRoute.path}
                            element={getRouteElement(appRoute)}
                        />
                    );
                })}

                <Route
                    path="*"
                    element={<NotFoundPage />}
                />
            </Routes>
        </>
    );
};